import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { appsApi } from 'src/api_services/apps';
import { SelectField } from 'src/components/SelectNew';

interface Props {
  currentNodeVersion?: string;
  setCurrentNodeVersion: (version: string) => void;
  error?: string;
  touched: boolean;
}

export const SelectableNodeVersion = ({ currentNodeVersion, setCurrentNodeVersion, error, touched }: Props) => {
  const [nodeVersions, setNodeVersions] = useState<string[]>([]);
  const [nodeVersionsLoading, setNodeVersionsLoading] = useState(false);
  const saveNodeVersion = useRef(setCurrentNodeVersion);

  useEffect(() => {
    saveNodeVersion.current = setCurrentNodeVersion;
  }, [setCurrentNodeVersion]);

  const fetchNodeVersions = useCallback(() => {
    setNodeVersionsLoading(true);
    appsApi
      .getGlobalPossibleNodeVersions({})
      .then((data) => {
        setNodeVersions(data.data);
      })
      .finally(() => setNodeVersionsLoading(false));
  }, []);

  useEffect(() => {
    // todo only when "node" is selected
    fetchNodeVersions();
  }, [fetchNodeVersions]);

  useEffect(() => {
    if (!nodeVersions.length) {
      return;
    }
    if (currentNodeVersion && nodeVersions.includes(currentNodeVersion)) {
      return;
    }
    // The select displays the first version while the field is still empty,
    // so the field gets that version too: otherwise the version shown as
    // chosen is not the version handed back to the form.
    saveNodeVersion.current(nodeVersions[0]);
  }, [nodeVersions, currentNodeVersion]);

  const nodeVersionOptions = [
    /* {
      label: 'Stable',
      value: 'stable',
     },*/
    ...nodeVersions.map((version) => ({
      label: version,
      value: version,
    })),
  ];

  return (
    <SelectField
      name="version"
      options={nodeVersionOptions}
      onChange={({ value }) => {
        setCurrentNodeVersion(value[0].value.toString());
      }}
      value={[
        nodeVersionOptions.find((version) => version.value === currentNodeVersion) ||
          nodeVersionOptions[0] || {
            label: undefined,
            value: undefined,
          },
      ]}
      label={<FormattedMessage id="node_js.form.node_js_version_label" defaultMessage="Node.js version" />}
      err={error}
      touched={touched}
      isLoading={nodeVersionsLoading}
      help={
        <FormattedMessage
          id="node_js.form.node_version_help"
          defaultMessage="Select the version of Node.js that your app requires."
        />
      }
    />
  );
};
